// ---------------------------------------------------------------------------
// FC4 §B.5 — wrapper ÚNICO de feedback de erro de API.
//
// É a única camada que chama `react-toastify` para erro vindo do backend.
// Consome só o `ApiError` do FC1 — nunca lê `response`/`errors` cru.
//
// Política de toast × inline (§B.3) derivada do `kind`:
//   validation com campo → NÃO faz toast (o form renderiza inline)
//   server (5xx)         → toast com o `errorId` como código de suporte
//   network              → toast com ação de retry, quando há handler
//   demais               → toast simples
//
// Páginas legadas continuam com seus `toast.error(err.message)` até serem
// tocadas (FC1 §A.10) — este wrapper é obrigatório só no código novo.
// ---------------------------------------------------------------------------

import { toast } from 'react-toastify';
import { apiErrorMessages } from '../errors/apiErrorMessages';
import { API_ERROR_KINDS } from '../errors/ApiError';

// Mensagem apresentável: dicionário por code → mensagem do backend → fallback.
export function apiErrorMessage(
   apiError,
   fallback = 'Ocorreu um erro. Tente novamente.'
) {
   if (!apiError) return fallback;
   return apiErrorMessages[apiError.code] ?? apiError.message ?? fallback;
}

/**
 * Exibe o erro de API conforme a política do FC4.
 *
 * @param {ApiError} apiError  erro normalizado pelo interceptor de contrato
 * @param {object}  [options]
 * @param {Function} [options.onRetry]  handler de retry (só usado em `network`)
 * @param {string}   [options.fallback] mensagem quando não há code nem message
 * @returns {import('react-toastify').Id | null} id do toast, ou null se inline
 */
export function notifyApiError(apiError, options = {}) {
   const { onRetry, fallback } = options;

   if (!apiError) return toast.error(fallback ?? 'Ocorreu um erro.');

   const message = apiErrorMessage(apiError, fallback);

   // 422 com campo vai INLINE, nunca em toast (§B.3). Sem campo mapeado não há
   // onde renderizar inline — aí o toast é a única saída, e ficar em silêncio
   // seria o catch silencioso que o §B.8 proíbe.
   if (apiError.kind === API_ERROR_KINDS.VALIDATION) {
      const hasFieldErrors = Object.keys(apiError.fieldErrors ?? {}).length > 0;
      if (hasFieldErrors) return null;
      return toast.error(message);
   }

   // 5xx SEMPRE exibe o error_id — é o que permite achar o evento no log do
   // backend (§B.7). Sem errorId no payload, mostra "—".
   if (apiError.kind === API_ERROR_KINDS.SERVER) {
      return toast.error(
         `${message} (código de suporte: ${apiError.errorId ?? '—'})`
      );
   }

   if (apiError.kind === API_ERROR_KINDS.NETWORK && typeof onRetry === 'function') {
      return toast.error(`${message} Clique para tentar novamente.`, {
         onClick: onRetry,
         autoClose: 8000,
      });
   }

   return toast.error(message);
}

export default notifyApiError;
